import { User, UserManager, UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts'

import { consumeReturnPath } from '../../utils/return-path.util'
import { IdentityProfile, IdentityProvider } from './identity-provider.interface'

export interface DexIdentityConfig {
  authority: string
  clientId: string
  redirectUri: string
  postLogoutRedirectUri: string
}

/** Dex OIDC via oidc-client-ts. No iframe session-monitor (Dex sends X-Frame-Options: DENY). */
export class DexIdentityProvider implements IdentityProvider {
  readonly providerType = 'dex' as const
  private manager: UserManager
  private user: User | null = null
  private tokenExpiredHandlers: Array<() => void> = []
  private authLogoutHandlers: Array<() => void> = []
  private refreshTimer: ReturnType<typeof setTimeout> | null = null

  constructor(config: DexIdentityConfig) {
    const settings: UserManagerSettings = {
      authority: config.authority,
      client_id: config.clientId,
      redirect_uri: config.redirectUri,
      post_logout_redirect_uri: config.postLogoutRedirectUri,
      response_type: 'code',
      // offline_access is what makes Dex issue a refresh token for signinSilent to use.
      scope: 'openid profile email groups offline_access',
      // scheduleRefresh owns renewal; a second renewer would only duplicate the call.
      automaticSilentRenew: false,
      monitorSession: false,
      userStore: new WebStorageStateStore({ store: window.localStorage })
    }
    this.manager = new UserManager(settings)

    this.manager.events.addUserSignedOut(() => {
      this.user = null
      this.fire(this.authLogoutHandlers)
    })
    this.manager.events.addAccessTokenExpired(() => {
      this.fire(this.tokenExpiredHandlers)
    })
  }

  async init(): Promise<boolean> {
    if (window.location.search.includes('code=') && window.location.search.includes('state=')) {
      try {
        this.user = await this.manager.signinRedirectCallback()
        // Strip callback params so a refresh doesn't re-trigger the exchange.
        const url = new URL(window.location.href)
        ;['code', 'state', 'session_state', 'iss'].forEach(k => url.searchParams.delete(k))
        window.history.replaceState({}, document.title, url.toString())
        // redirectUri is pinned to the tenant root, so replay the asked-for route; runs pre-router.
        const returnPath = consumeReturnPath()
        if (returnPath) window.history.replaceState({}, document.title, returnPath)
      } catch {
        this.user = null
      }
    } else {
      this.user = await this.manager.getUser()
    }
    if (this.user && !this.user.expired) {
      this.scheduleRefresh()
      return true
    }
    return false
  }

  isAuthenticated(): boolean {
    return Boolean(this.user && !this.user.expired)
  }

  isTokenExpired(): boolean {
    return Boolean(this.user?.expired)
  }

  async login(options?: { promptCredentials?: boolean }): Promise<void> {
    await this.manager.signinRedirect(
      options?.promptCredentials ? { extraQueryParams: { prompt: 'login' } } : undefined
    )
  }

  async logout(redirectUri: string): Promise<void> {
    // Dex 2.45 discovery may omit end_session_endpoint → signoutRedirect throws;
    // fall back to local removeUser + hard nav.
    try {
      await this.manager.signoutRedirect({ post_logout_redirect_uri: redirectUri })
    } catch {
      await this.manager.removeUser()
      window.location.href = redirectUri
    }
  }

  getToken(): string | undefined {
    return this.user?.access_token
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- IdentityProvider contract; Dex refreshes via signinSilent
  async refreshToken(_minValiditySeconds: number): Promise<boolean> {
    try {
      const refreshed = await this.manager.signinSilent()
      if (refreshed) {
        this.user = refreshed
        this.scheduleRefresh()
        return true
      }
      return false
    } catch (err) {
      this.fire(this.tokenExpiredHandlers)
      throw err
    }
  }

  getProfile(): IdentityProfile | undefined {
    if (!this.user) return undefined
    const claims = this.user.profile as Record<string, unknown>
    const groups = Array.isArray(claims['groups']) ? (claims['groups'] as string[]) : []
    return {
      id: (claims['sub'] as string) ?? '',
      username: (claims['preferred_username'] as string) ?? (claims['email'] as string),
      email: claims['email'] as string,
      fullName: claims['name'] as string,
      roles: groups
    }
  }

  onTokenExpired(cb: () => void): void {
    this.tokenExpiredHandlers.push(cb)
  }

  onAuthLogout(cb: () => void): void {
    this.authLogoutHandlers.push(cb)
  }

  private scheduleRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    }
    if (!this.user) return
    // Refresh 30s before expiry — mirrors keycloak-js updateToken(30).
    const expiresAtMs = (this.user.expires_at ?? 0) * 1000
    const refreshAt = expiresAtMs - 30_000 - Date.now()
    if (refreshAt <= 0) {
      this.refreshToken(30).catch(() => {
        /* ignore */
      })
      return
    }
    this.refreshTimer = setTimeout(() => {
      this.refreshToken(30).catch(() => {
        /* ignore */
      })
    }, refreshAt)
  }

  private fire(handlers: Array<() => void>): void {
    for (const h of handlers) {
      try {
        h()
      } catch {
        // Swallow handler errors; one failing listener shouldn't kill others.
      }
    }
  }
}
